<template>
  <el-dialog
    :visible.sync="show"
    width="800px"
    custom-class="openCodeDialog"
    center
    append-to-body
    :close-on-click-modal="false"
    :show-close="true"
    @closed="handleClosed"
  >
    <!-- 审核成功：已发货 -->
    <template v-if="status === STATUS.SUCCESS">
      <div class="statusBanner statusBanner--success">
        恭喜，您的商户授权物料申请已审核成功。
      </div>
      <div class="successBody">
        <div class="successIcon">
          <i class="el-icon-check"></i>
        </div>
        <div class="successTitle">
          {{
            confirmReceive === 2
              ? "合作伙伴授权物料已确认收货"
              : "合作伙伴授权物料已发货"
          }}
        </div>
        <div class="successDesc">
          {{
            confirmReceive === 2
              ? "您已确认收件，感谢您的合作！"
              : "请确认是否收到签约物资，若已收到，请确认收货"
          }}
        </div>
        <div class="openCodeFooter" v-if="confirmReceive !== 2">
          <el-button
            type="primary"
            class="openCodeSubmitBtn"
            :loading="confirming"
            @click="handleConfirmReceipt"
          >
            确认收货
          </el-button>
        </div>
      </div>
    </template>

    <!-- 表单：待提交 / 审核中 / 审核失败 -->
    <template v-else>
      <div
        v-if="status === STATUS.PENDING"
        class="statusBanner statusBanner--pending"
      >
        您的盘厂授权物料申请正在审核中，请耐心等待。
      </div>
      <div
        v-else-if="status === STATUS.REJECTED"
        class="statusBanner statusBanner--rejected"
      >
        您的盘厂授权物料申请审核未通过。原因：{{ rejectReason || "—" }}
      </div>

      <h2 class="openCodeTitle">合作伙伴授权物料申请</h2>

      <!-- 已完成权益 Tab 切换 -->
      <div v-if="tabList && tabList.length > 0" class="equityTabContainer">
        <div
          v-for="item in tabList"
          :key="item.id || item.name"
          :class="[
            'equityTabItem',
            { active: activeTab === (item.name || item.id) },
          ]"
          @click="handleTabClick(item.name || item.id)"
        >
          {{ item.name }}
        </div>
      </div>

      <el-form
        ref="materialForm"
        :model="form"
        :rules="rules"
        label-width="80px"
        class="openCodeForm"
        :disabled="formDisabled"
      >
        <div class="openCodeFields">
          <el-form-item label="审批人" prop="approver">
            <el-select
              v-model="form.approver"
              placeholder="请输入名称搜索并选择审批人"
              class="openCodeFull"
              popper-class="openCodePopper"
              :popper-append-to-body="true"
              filterable
              :loading="approverSearchLoading"
              @change="handleApproverChange"
            >
              <el-option
                v-for="item in approverOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="所在大区" prop="region">
            <el-select
              v-model="form.region"
              placeholder="请选择所在大区"
              class="openCodeFull"
              popper-class="openCodePopper"
              :popper-append-to-body="true"
              disabled
            >
              <el-option
                v-for="item in regionOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="收件人" prop="recipient">
            <el-input
              v-model="form.recipient"
              placeholder="请填写"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item label="联系方式" prop="contactPhone">
            <el-input
              v-model="form.contactPhone"
              placeholder="请填写"
              maxlength="20"
            />
          </el-form-item>
          <el-form-item label="收件地址" prop="shippingAddress">
            <el-input
              v-if="formDisabled"
              v-model="form.shippingAddress"
              disabled
            />
            <el-cascader
              v-else
              ref="addressCascader"
              v-model="addressCodes"
              :options="mapProvinceData"
              class="openCodeFull openCodeCascader"
              :props="{
                expandTrigger: 'hover',
                value: 'name',
                label: 'name',
                children: 'districts',
              }"
              placeholder="省/市/区/街道"
              popper-class="openCodePopper"
              :append-to-body="true"
              clearable
              @change="handleAddressChange"
            />
          </el-form-item>
          <el-form-item label="详细地址" prop="detailAddress">
            <el-input
              v-model="form.detailAddress"
              placeholder="详细地址"
              maxlength="200"
            />
          </el-form-item>
        </div>

        <div v-if="!formDisabled" class="openCodeFooter">
          <el-button
            type="primary"
            class="openCodeSubmitBtn"
            :loading="submitting"
            @click="handleSubmit"
          >
            提交申请
          </el-button>
        </div>
      </el-form>
    </template>
  </el-dialog>
</template>

<script>
import {
  getCompletedEquityList,
  getMaterialApplyDetail,
  addMaterialApply,
  updateMaterialApply,
  materialApplyReceive,
  getAuditorRegionList,
  searchAuditor,
} from "@/api/lx";
import { fetchMapData } from "@/utils/mapData";

const STATUS = {
  FORM: "form",
  PENDING: "pending",
  REJECTED: "rejected",
  SUCCESS: "success",
};

const AUDIT_STATUS_MAP = {
  1: STATUS.PENDING,
  2: STATUS.SUCCESS,
  3: STATUS.REJECTED,
};

export default {
  data() {
    return {
      STATUS,
      show: false,
      fetching: false,
      submitting: false,
      confirming: false,
      status: STATUS.FORM,
      confirmReceive: 1,
      rejectReason: "",
      applyId: null,
      regionOptions: [],
      approverOptions: [],
      approverSearchLoading: false,
      mapProvinceData: [],
      mapDataLoading: false,
      addressCodes: [],
      prevShippingAddress: "",
      tabList: [], // 已完成权益分类 Tab 列表
      activeTab: "", // 当前选中的权益名称（如 L1, L2）
      areaCascaderProps: {
        expandTrigger: "hover",
        lazy: true,
      },
      form: {
        region: "",
        approver: "",
        approverId: "",
        recipient: "",
        contactPhone: "",
        shippingAddress: "",
        detailAddress: "",
        province: "",
        city: "",
        district: "",
        street: "",
      },
      rules: {
        region: [
          { required: true, message: "请选择所在大区", trigger: "change" },
        ],
        approver: [
          { required: true, message: "请选择审批人", trigger: "change" },
        ],
        recipient: [
          { required: true, message: "请填写收件人", trigger: "blur" },
        ],
        contactPhone: [
          { required: true, message: "请填写联系方式", trigger: "blur" },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: "手机号格式不正确",
            trigger: "blur",
          },
        ],
        shippingAddress: [
          {
            validator: (rule, value, callback) => {
              if (!this.addressCodes || this.addressCodes.length < 3) {
                callback(new Error("请选择"));
                return;
              }
              callback();
            },
            trigger: "change",
          },
        ],
        detailAddress: [
          { required: true, message: "请填写详细地址", trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    formDisabled() {
      return this.status === STATUS.PENDING;
    },
  },
  created() {
    this.areaCascaderProps.lazyLoad = this.loadAreaCascaderNode;
    this.loadMapData();
    this.fetchRegionList();
    this.searchApproverMethod("");
  },
  methods: {
    /**
     * 打开物料申请弹窗，获取已完成权益集合列表，默认选中第一个 Tab 并加载表单详情
     * @returns {void}
     */
    openShow() {
      this.show = true;
      if (this.fetching) return;
      this.resetOpenState();
      this.searchApproverMethod("");

      this.fetching = true;
      getCompletedEquityList()
        .then((res) => {
          if (res && (res.code === 200 || res.code === 0 || res.success)) {
            this.tabList = res.data || [];
          } else {
            this.tabList = [];
          }
          if (this.tabList.length > 0) {
            const firstTab = this.tabList[0].name || this.tabList[0].id;
            this.activeTab = firstTab;
            return this.fetchDetailByEquity(firstTab);
          } else {
            this.activeTab = "";
            return this.fetchDetailByEquity("");
          }
        })
        .catch(() => {
          this.tabList = [];
          this.activeTab = "";
          return this.fetchDetailByEquity("");
        });
    },

    /**
     * 根据指定的权益等级名称获取物料申请详情表单
     * @param {string} equityLevelName - 权益等级名称（如 "L1", "L2"）
     * @returns {Promise<void>}
     */
    /**
     * 根据指定的权益等级名称获取物料申请详情表单
     * @param {string} equityLevelName - 权益等级名称（如 "L1", "L2"）
     * @returns {Promise<void>}
     */
    fetchDetailByEquity(equityLevelName) {
      this.fetching = true;
      const params = equityLevelName ? { equityLevelName } : {};
      return getMaterialApplyDetail(params)
        .then((res) => {
          this.applyDetailFromResponse(res);
        })
        .catch((err) => {
          console.error("【物料详情】接口请求失败:", err);
          this.resetFormState();
        })
        .finally(() => {
          this.fetching = false;
        });
    },

    /**
     * 点击权益 Tab 进行切换
     * @param {string} tabName - 权益 Tab 名称
     * @returns {void}
     */
    handleTabClick(tabName) {
      if (this.activeTab === tabName) return;
      this.activeTab = tabName;
      // 切换 Tab 时先清除当前表单的校验提示
      if (this.$refs.materialForm) {
        this.$refs.materialForm.clearValidate();
      }
      this.fetchDetailByEquity(tabName);
    },

    /**
     * 关闭弹窗
     * @returns {void}
     */
    closeModal() {
      this.show = false;
    },

    /**
     * 弹窗关闭动画结束后的回调处理
     * @returns {void}
     */
    handleClosed() {
      this.resetOpenState();
    },

    /**
     * 解析物料申请接口返回的详情数据
     * @param {Object} res - 接口响应对象
     * @returns {void}
     */
    applyDetailFromResponse(res) {
      const data = res && res.data;
      if (!data) {
        // 当该权益等级没有回显详情数据（尚未申请）时，立即重置表单为置空状态
        this.resetFormState();
        return;
      }
      this.status = this.resolveStatus(data.auditStatus);
      this.fillFormFromDetail(data);
    },

    /**
     * 清空重置表单字段与状态（保留 Tab 列表与当前选中 Tab）
     * @returns {void}
     */
    resetFormState() {
      this.status = STATUS.FORM;
      this.confirmReceive = 1;
      this.applyId = null;
      this.rejectReason = "";
      this.submitting = false;
      this.confirming = false;
      this.addressCodes = [];
      this.prevShippingAddress = "";
      this.form = {
        region: "",
        approver: "",
        approverId: "",
        recipient: "",
        contactPhone: "",
        shippingAddress: "",
        detailAddress: "",
        province: "",
        city: "",
        district: "",
        street: "",
      };

      // 关键：在 Vue DOM 异步更新与组件内部事件触发后，清除所有误报的校验警告
      this.$nextTick(() => {
        if (this.$refs.materialForm) {
          this.$refs.materialForm.clearValidate();
        }
      });
    },

    /**
     * 重置弹窗所有状态（包含 Tab 列表与表单）
     * @returns {void}
     */
    resetOpenState() {
      this.tabList = [];
      this.activeTab = "";
      this.resetFormState();
    },
    cleanEmptyDistricts(list) {
      if (!list || !list.length) return;
      list.forEach((item) => {
        if (item.districts && item.districts.length === 0) {
          delete item.districts;
        } else if (item.districts && item.districts.length > 0) {
          this.cleanEmptyDistricts(item.districts);
        }
      });
    },
    loadMapData() {
      this.mapDataLoading = true;
      fetchMapData()
        .then((data) => {
          const list = JSON.parse(JSON.stringify(data.provinceData || []));
          this.cleanEmptyDistricts(list);
          this.mapProvinceData = list;
        })
        .catch(() => {
          this.$message.error("地址数据加载失败，请稍后重试");
        })
        .finally(() => {
          this.mapDataLoading = false;
        });
    },
    buildAreaNodeValue(parentPath, item) {
      return parentPath ? `${parentPath}/${item.name}` : item.name;
    },
    loadAreaCascaderNode(node, resolve) {
      console.log(
        "【调试地图加载】进入 lazyLoad, level:",
        node.level,
        "value:",
        node.value
      );
      const render = () => {
        let list = [];
        if (node.root || node.level === 0) {
          list = this.mapProvinceData;
        } else if (node.data && node.data.raw) {
          list = node.data.raw.districts || node.data.raw.children || [];
        }
        const parentPath =
          node.root || node.level === 0
            ? ""
            : (node.data && node.data.value) || "";
        const options = list.map((item) => {
          const value = this.buildAreaNodeValue(parentPath, item);
          const hasChildren =
            (item.districts && item.districts.length > 0) ||
            (item.children && item.children.length > 0);
          return {
            value,
            label: item.name,
            leaf: !hasChildren,
            raw: item,
          };
        });
        if (node.level === 0) {
          console.log(
            "【调试省份列表】所有省份的 value 为:",
            JSON.stringify(options.map((o) => o.value))
          );
        }
        console.log(
          "【调试地图加载】完成 resolve, level:",
          node.level,
          "options数量:",
          options.length,
          "第一项的值:",
          options[0] ? options[0].value : "none"
        );
        resolve(options);
      };

      if (this.mapProvinceData.length) {
        render();
        return;
      }

      fetchMapData()
        .then((data) => {
          this.mapProvinceData = data.provinceData || [];
          render();
        })
        .catch(() => resolve([]));
    },
    handleAddressChange() {
      if (this.addressCodes && this.addressCodes.length > 0) {
        this.form.province = this.addressCodes[0] || "";
        this.form.city = this.addressCodes[1] || "";
        this.form.district = this.addressCodes[2] || "";
        this.form.street = this.addressCodes[3] || "";

        let addr = `${this.form.province}/${this.form.city}/${this.form.district}`;
        if (this.form.street) {
          addr += `/${this.form.street}`;
        }
        this.form.shippingAddress = addr;
      } else {
        this.form.shippingAddress = "";
        this.form.province = "";
        this.form.city = "";
        this.form.district = "";
        this.form.street = "";
      }
      this.$refs.materialForm &&
        this.$refs.materialForm.validateField("shippingAddress");
    },
    resolveStatus(raw) {
      if (raw === undefined || raw === null || raw === "") {
        return STATUS.FORM;
      }
      const key = typeof raw === "string" ? raw.toLowerCase() : raw;
      return AUDIT_STATUS_MAP[key] || STATUS.FORM;
    },
    fillFormFromDetail(data) {
      if (!data || typeof data !== "object") return;
      this.applyId = data.id || data.applyId || null;
      this.confirmReceive =
        data.confirmReceive !== undefined ? data.confirmReceive : 1;
      this.rejectReason = data.rejectReason || data.auditReason || "";
      this.form.region = data.region || data.bigRegion || "";
      this.form.approver = data.approver || data.approverName || "";
      this.form.approverId = data.approverId || "";

      // 兜底动态加入大区和审批人选项以确保回显显示正确名称
      if (this.form.region) {
        const exists = this.regionOptions.some(
          (o) => o.value === this.form.region
        );
        if (!exists) {
          this.regionOptions.push({
            label: this.form.region,
            value: this.form.region,
          });
        }
      }
      if (this.form.approver) {
        const exists = this.approverOptions.some(
          (o) => o.value === this.form.approver
        );
        if (!exists) {
          this.approverOptions.push({
            label: this.form.approver,
            value: this.form.approver,
            id: this.form.approverId || "",
          });
        }
      }

      this.form.recipient = data.recipient || data.receiver || "";
      this.form.contactPhone =
        data.contactPhone || data.mobile || data.phone || "";
      this.form.detailAddress = data.detailAddress || data.addressDetail || "";
      this.form.province = data.province || "";
      this.form.city = data.city || "";
      this.form.district = data.district || "";
      this.form.street = data.street || "";

      if (this.form.province && this.form.city && this.form.district) {
        let addr = `${this.form.province}/${this.form.city}/${this.form.district}`;
        if (this.form.street) {
          addr += `/${this.form.street}`;
        }
        this.form.shippingAddress = addr;
      } else {
        let addr = data.shippingAddress || data.receiveAddress || "";
        if (this.form.street && addr && !addr.includes(this.form.street)) {
          addr += `/${this.form.street}`;
        }
        this.form.shippingAddress = addr;
      }

      // 记录上次提交的地址，供回显参考提示
      this.prevShippingAddress = this.form.shippingAddress;

      // 等待 mapProvinceData 加载后，在地图中逐级验证路径
      // 关键：只有路径终点是叶子节点才设置 addressCodes，否则清空
      // 非叶子路径会让 el-cascader 自动展开下一级面板，造成"还要再选"的错觉
      const rawCodes = [
        this.form.province,
        this.form.city,
        this.form.district,
        this.form.street,
      ].filter(Boolean);

      const resolveLeafCodes = (mapData) => {
        const validCodes = [];
        let currentList = mapData;
        let lastFound = null;
        for (const code of rawCodes) {
          const found =
            currentList && currentList.find((item) => item.name === code);
          if (!found) break;
          validCodes.push(code);
          lastFound = found;
          currentList = found.districts || [];
        }
        // 当完全匹配了原有的所有级，且匹配到的最后一级已经没有子节点（或者是空子节点）时，视作合法的叶子级回显
        const isLeaf =
          lastFound &&
          validCodes.length === rawCodes.length &&
          (!lastFound.districts || lastFound.districts.length === 0);

        return isLeaf ? validCodes : [];
      };

      const doSetCodes = (mapData) => {
        this.addressCodes = resolveLeafCodes(mapData);
      };

      if (this.mapProvinceData && this.mapProvinceData.length > 0) {
        this.$nextTick(() => doSetCodes(this.mapProvinceData));
      } else {
        const unwatch = this.$watch("mapProvinceData", (val) => {
          if (val && val.length > 0) {
            this.$nextTick(() => doSetCodes(val));
            unwatch();
          }
        });
      }

      // 填充表单数据后清除可能的误报校验信息
      this.$nextTick(() => {
        if (this.$refs.materialForm) {
          this.$refs.materialForm.clearValidate();
        }
      });
    },
    fetchRegionList() {
      getAuditorRegionList().then((res) => {
        if (res.code === 200) {
          const list = res.data || [];
          this.regionOptions = list.map((item) => {
            const label = item.name || item.regionName || item.label || item;
            const value = item.name || item.regionName || item.value || item;
            return { label, value };
          });
        }
      });
    },
    searchApproverMethod(query) {
      this.approverSearchLoading = true;
      searchAuditor({ name: query || "" })
        .then((res) => {
          if (res.code === 200) {
            const list = res.data || [];
            this.approverOptions = list.map((item) => {
              return {
                label: item.name,
                value: item.name,
                id: item.id,
                region: item.region,
              };
            });
          } else {
            this.approverOptions = [];
          }
        })
        .catch(() => {
          this.approverOptions = [];
        })
        .finally(() => {
          this.approverSearchLoading = false;
        });
    },
    handleApproverChange(val) {
      const option = this.approverOptions.find((item) => item.value === val);
      if (option) {
        this.form.approverId = option.id || "";
        if (option.region) {
          this.form.region = option.region;
        }
      } else {
        this.form.approverId = "";
      }
    },
    /**
     * 提交或更新物料申请表单数据
     * @returns {void}
     */
    handleSubmit() {
      this.$refs.materialForm.validate((valid) => {
        if (!valid) return;
        this.submitting = true;

        const payload = {
          equityLevelName: this.activeTab || "",
          region: this.form.region,
          approver: this.form.approver,
          approverId: this.form.approverId,
          recipient: this.form.recipient,
          contactPhone: this.form.contactPhone,
          province: this.form.province,
          city: this.form.city,
          district: this.form.district,
          street: this.form.street,
          detailAddress: this.form.detailAddress,
        };

        const apiCall = this.applyId
          ? updateMaterialApply(this.applyId, payload)
          : addMaterialApply(payload);

        apiCall
          .then((res) => {
            if (res && (res.code == 200 || res.code == 0 || res.success)) {
              this.$message.success("申请已提交");
              this.applyId = res.data;
              this.show = false;
            }
          })
          .catch(() => {})
          .finally(() => {
            this.submitting = false;
          });
      });
    },

    /**
     * 确认收货操作
     * @returns {void}
     */
    handleConfirmReceipt() {
      this.confirming = true;
      materialApplyReceive(this.applyId)
        .then(() => {
          this.$message.success("已确认收货");
          this.closeModal();
          this.$root.$emit("refreshUserInfo");
        })
        .catch(() => {})
        .finally(() => {
          this.confirming = false;
        });
    },
  },
};
</script>

<style lang="less" scoped>
.statusBanner {
  margin: -24px -32px 24px;
  padding: 10px 32px;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.statusBanner--pending {
  background: #fff7e6;
  color: #d46b08;
}

.statusBanner--rejected {
  background: #fff1f0;
  color: #cf1322;
}

.statusBanner--success {
  background: #e6fffb;
  color: #cf1322;
}

.openCodeTitle {
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  line-height: 1.4;
}

.equityTabContainer {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.equityTabItem {
  padding: 6px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #525f66;
  background: #f3f4f6;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    color: #00d8c3;
    background: #e6faf7;
  }

  &.active {
    color: #ffffff;
    background: #00d8c3;
    font-weight: 600;
  }
}

.openCodeForm {
  width: 100%;
}

.openCodeFields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 48px;
}

.openCodeFull {
  width: 100%;
}

.openCodeFooter {
  margin-top: 32px;
  display: flex;
  justify-content: center;
}

.openCodeSubmitBtn {
  min-width: 200px;
  height: 40px;
  padding: 0 80px;
  background: #00d8c3;
  border-color: #00d8c3;
  font-size: 15px;
  border-radius: 4px;
  &:hover,
  &:focus {
    background: #00c2b0;
    border-color: #00c2b0;
  }
}

.successBody {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px 16px;
  text-align: center;
}

.successIcon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #b7eb8f;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  i {
    font-size: 36px;
    color: #fff;
    font-weight: bold;
  }
}

.successTitle {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.4;
  margin-bottom: 12px;
}

.successDesc {
  font-size: 14px;
  color: #9ca3af;
  line-height: 1.4;
  margin-bottom: 28px;
  max-width: 360px;
}

/deep/ .el-form-item__label {
  font-size: 14px;
  color: #525f66;
  line-height: 40px;
}
/deep/ .el-form-item__label::before {
  color: #f5222d;
}
/deep/ .el-input__inner,
/deep/ .el-select .el-input__inner {
  height: 40px;
  line-height: 40px;
  border-radius: 8px;
}
/deep/ .el-form-item.is-disabled .el-input__inner,
/deep/ .el-form-item.is-disabled .el-input.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #606266;
}
.openCodeCascader {
  /deep/ .el-input__inner {
    padding-left: 15px;
  }
}

@media (max-width: 768px) {
  .openCodeFields {
    grid-template-columns: 1fr;
    row-gap: 12px;
  }
  .openCodeForm {
    /deep/ .el-form-item {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      margin-bottom: 16px;
    }
    /deep/ .el-form-item__label {
      text-align: left;
      float: none;
      display: inline-block;
      width: auto !important;
      line-height: 24px;
      padding: 0 0 4px;
    }
    /deep/ .el-form-item__content {
      margin-left: 0 !important;
    }
  }
}
</style>

<style lang="less">
.openCodeDialog {
  width: 880px !important;
  max-width: 95% !important;
  margin: 0 auto !important;
  top: 50% !important;
  transform: translateY(-50%);
  border-radius: 8px;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border: none;
  }
  .el-dialog__title {
    display: none;
  }
  .el-dialog__headerbtn {
    top: 14px;
    right: 14px;
    z-index: 2;
    font-size: 18px;
  }
  .el-dialog__body {
    padding: 24px 32px 32px;
    max-height: calc(90vh - 80px);
    overflow-y: auto;
  }
}

.openCodePopper {
  z-index: 3001 !important;
}
</style>
